import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { api, formatApiError } from "../../lib/api";
import { PageHeader, ResponsiveTable } from "./ui";
import { FormFields, buildPayload } from "./FormFields";

const useOptions = (fields) => {
  const [map, setMap] = useState({});
  useEffect(() => {
    fields.filter((f) => f.optionsUrl).forEach((f) => {
      api.get(f.optionsUrl).then((r) => setMap((m) => ({ ...m, [f.name]: r.data.map(f.mapOption) }))).catch(() => {});
    });
  }, [fields]);
  return map;
};

const EntityDialog = ({ state, onClose, onSaved, testid }) => {
  const [form, setForm] = useState(state.initial);
  const [saving, setSaving] = useState(false);
  const optionsMap = useOptions(state.fields);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = buildPayload(state.fields, form);
      if (state.mode === "create") await api.post(state.url, payload);
      else await api.patch(state.url, payload);
      toast.success(state.mode === "create" ? "Data berhasil ditambahkan" : "Perubahan disimpan");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid={`${testid}-dialog`}>
        <DialogHeader><DialogTitle>{state.title}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-6">
          <FormFields fields={state.fields} form={form} setForm={setForm} optionsMap={optionsMap} prefix={testid} />
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid={`${testid}-cancel-btn`}>Batal</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-800 hover:bg-emerald-900" data-testid={`${testid}-save-btn`}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Simpan
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const initialFrom = (fields, row, defaults = {}) =>
  Object.fromEntries(fields.map((f) => [f.name, row ? row[f.name] ?? "" : defaults[f.name] ?? ""]));

export default function CrudPage({
  title, subtitle, testid, listUrl, createUrl, updateUrl, columns, createFields, editFields, extraActions,
  searchKeys = [], createLabel = "Tambah", defaults, embedded = false, summary, stackActions = false,
}) {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null);

  const load = useCallback(() => {
    api.get(listUrl).then((r) => setRows(r.data)).catch((e) => { toast.error(formatApiError(e)); setRows([]); });
  }, [listUrl]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => searchKeys.some((k) => String(r[k] ?? "").toLowerCase().includes(s)));
  }, [rows, q, searchKeys]);

  const openCreate = () => setDialog({ mode: "create", title: createLabel, url: createUrl || listUrl, fields: createFields, initial: initialFrom(createFields, null, defaults) });
  const openEdit = (row) => setDialog({ mode: "edit", title: "Ubah Data", url: updateUrl(row), fields: editFields, initial: initialFrom(editFields, row) });

  const actions = (editFields || extraActions)
    ? (row, p) => (
      <div className={stackActions ? "grid gap-1.5 md:[&_button]:w-full md:[&_button]:justify-center max-md:inline-flex max-md:flex-wrap max-md:justify-end" : "inline-flex flex-wrap gap-2 justify-end"}>
        {extraActions?.(row, load, p)}
        {editFields && (
          <Button size="sm" variant="outline" onClick={() => openEdit(row)} data-testid={`${p}${testid}-edit-${row.id}`}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Ubah
          </Button>
        )}
      </div>
    )
    : null;

  const addBtn = createFields ? (
    <Button onClick={openCreate} className="bg-emerald-800 hover:bg-emerald-900 rounded-full" data-testid={`${testid}-create-btn`}>
      <Plus className="h-4 w-4 mr-1.5" /> {createLabel}
    </Button>
  ) : null;

  return (
    <div data-testid={`${testid}-page`}>
      {embedded ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <h2 className="text-base md:text-lg font-semibold text-slate-900">{title}</h2>{addBtn}
        </div>
      ) : (
        <PageHeader title={title} subtitle={subtitle} action={addBtn} />
      )}
      {summary && rows && summary(rows)}
      {searchKeys.length > 0 && (
        <div className="relative mb-5 max-w-sm">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari..." className="pl-9 bg-white" data-testid={`${testid}-search-input`} />
        </div>
      )}
      {rows === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-700" /></div>
      ) : (
        <ResponsiveTable columns={columns} rows={filtered} testid={`${testid}-table`} actions={actions} stackActions={stackActions} />
      )}
      {dialog && <EntityDialog state={dialog} testid={`${testid}-form`} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}
